/**
 * Typed client for the advanced report routes (`/v1/audit-remediation`, F0 contract; T3 fills the report data).
 * The page talks to an `AdvancedReportApi`; production uses the BFF (`bffAdvancedReportApi`), tests and previews use the
 * fixture-backed `createMockAdvancedReportApi` (same error envelopes as the API, so error mapping is exercised too).
 */
import {
  canTransitionFinding, CreateReportViewRequestSchema, MAX_REPORT_VIEWS, UpdateFindingStatusRequestSchema, UpdateReportViewRequestSchema,
  OWNER_ONLY_STATUSES,
  type AdvancedReport, type CreateReportViewRequest, type FindingState, type FindingStatusChange, type ReportView,
  type UpdateFindingStatusRequest, type UpdateReportViewRequest,
} from '@qm/contracts/audit-remediation';
import type { ErrorEnvelope } from '@qm/contracts';
import { bff, BffError, type FetchLike } from '../../../../../lib/bff';

export const ADVANCED_REPORT_FLAG = 'audit.advanced_report';
const BASE = '/v1/audit-remediation' as const;
const enc = encodeURIComponent;

export interface AdvancedReportApi {
  getReport(jobId: string): Promise<AdvancedReport>;
  getHistory(jobId: string, findingId: string): Promise<FindingStatusChange[]>;
  updateStatus(jobId: string, findingId: string, body: UpdateFindingStatusRequest): Promise<FindingState>;
  listViews(): Promise<ReportView[]>;
  createView(body: CreateReportViewRequest): Promise<ReportView>;
  updateView(id: string, body: UpdateReportViewRequest): Promise<ReportView>;
  deleteView(id: string): Promise<void>;
}

/** Production client: browser -> same-origin BFF -> qm-api. */
export function bffAdvancedReportApi(fetchFn?: FetchLike): AdvancedReportApi {
  const call = <T,>(path: `/v1/${string}`, method = 'GET', body?: unknown) =>
    bff<T>(path, { method, ...(body === undefined ? {} : { body: JSON.stringify(body) }) }, fetchFn);
  return {
    getReport: (jobId) => call<AdvancedReport>(`${BASE}/jobs/${enc(jobId)}/report`),
    getHistory: (jobId, findingId) => call<FindingStatusChange[]>(`${BASE}/jobs/${enc(jobId)}/findings/${enc(findingId)}/history`),
    updateStatus: (jobId, findingId, body) => call<FindingState>(`${BASE}/jobs/${enc(jobId)}/findings/${enc(findingId)}/status`, 'PATCH', body),
    listViews: () => call<ReportView[]>(`${BASE}/views`),
    createView: (body) => call<ReportView>(`${BASE}/views`, 'POST', body),
    updateView: (id, body) => call<ReportView>(`${BASE}/views/${enc(id)}`, 'PATCH', body),
    deleteView: async (id) => { await call<unknown>(`${BASE}/views/${enc(id)}`, 'DELETE'); },
  };
}

// ---------------- errors ----------------

/** The report is behind a flag: the API answers 404 `feature_disabled` when it is off. */
export const isFlagOff = (e: unknown) => e instanceof BffError && e.status === 404 && e.envelope?.error.code === 'feature_disabled';
export const isNotFound = (e: unknown) => e instanceof BffError && e.status === 404 && !isFlagOff(e);

/** API error -> i18n key. The API names its own message key for known rules (`details.messageKey`). */
export function errorKeyOf(e: unknown): string {
  if (e instanceof BffError) {
    const d = (e.envelope?.error.details ?? {}) as Record<string, unknown>;
    const mk = d.messageKey;
    if (typeof mk === 'string' && mk.startsWith('audit-remediation.')) return mk;
    const reason = d.reason;
    if (reason === 'transition_not_allowed') return 'audit-remediation.errors.transitionNotAllowed';
    if (reason === 'version_conflict') return 'audit-remediation.errors.versionConflict';
    if (reason === 'view_name_taken') return 'audit-remediation.views.nameTaken';
    if (reason === 'view_limit') return 'audit-remediation.views.limit';
    if (e.status === 403) return 'audit-remediation.report.errors.forbidden';
    if (e.status === 400) return 'audit-remediation.report.errors.invalid';
  }
  return 'audit-remediation.report.errors.generic';
}

// ---------------- fixture-backed mock ----------------

const envelope = (code: string, message: string, details?: Record<string, unknown>): ErrorEnvelope =>
  ({ error: { code, message, correlationId: 'corr_mock', ...(details ? { details } : {}) } }) as ErrorEnvelope;
const fail = (status: number, code: string, message: string, details?: Record<string, unknown>) => new BffError(status, envelope(code, message, details));

export interface MockApiOptions {
  roles?: readonly string[];
  userId?: string;
  flagOn?: boolean;
  views?: ReportView[];
  now?: () => string;
  /** Make the next call of this method fail with a generic 500. */
  failNext?: keyof AdvancedReportApi;
}

/**
 * In-memory API over a report fixture. Enforces the same rules as F0: customer transitions, reason >= 5 chars,
 * owner-only dismiss/accept, unique view names, one default view, max views, member edits own views only.
 */
export function createMockAdvancedReportApi(report: AdvancedReport, opts: MockApiOptions = {}) {
  const roles = opts.roles ?? ['customer_owner'];
  const userId = opts.userId ?? 'usr_synthetic';
  const isOwner = roles.some((r) => r === 'customer_owner' || r === 'customer_admin');
  const canWrite = isOwner || roles.includes('customer_member');
  const now = opts.now ?? (() => '2026-10-31T09:00:00.000Z');
  let data: AdvancedReport = structuredClone(report);
  let views: ReportView[] = structuredClone(opts.views ?? []);
  const history = new Map<string, FindingStatusChange[]>();
  const versions = new Map<string, number>();
  const calls: { method: keyof AdvancedReportApi; args: unknown[] }[] = [];
  let failNext = opts.failNext ?? null;
  let seq = 0;
  const state = { flagOn: opts.flagOn ?? true };

  const guard = (method: keyof AdvancedReportApi, args: unknown[]) => {
    calls.push({ method, args });
    if (!state.flagOn) throw fail(404, 'feature_disabled', 'Feature disabled');
    if (failNext === method) { failNext = null; throw fail(500, 'internal', 'Mock failure'); }
  };
  const job = (jobId: string) => { if (jobId !== data.jobId) throw fail(404, 'not_found', 'Not found'); };
  const finding = (id: string) => { const f = data.findings.find((x) => x.id === id); if (!f) throw fail(404, 'not_found', 'Not found'); return f; };
  const view = (id: string) => { const v = views.find((x) => x.id === id); if (!v) throw fail(404, 'not_found', 'Not found'); return v; };
  const nameTaken = (name: string, except?: string) => views.some((v) => v.id !== except && v.name.toLowerCase() === name.toLowerCase());

  const api: AdvancedReportApi = {
    async getReport(jobId) { guard('getReport', [jobId]); job(jobId); return structuredClone(data); },
    async getHistory(jobId, findingId) { guard('getHistory', [jobId, findingId]); job(jobId); finding(findingId); return structuredClone(history.get(findingId) ?? []); },
    async updateStatus(jobId, findingId, raw) {
      guard('updateStatus', [jobId, findingId, raw]); job(jobId);
      if (!canWrite) throw fail(403, 'forbidden', 'Forbidden');
      const parsed = UpdateFindingStatusRequestSchema.safeParse(raw);
      if (!parsed.success) throw fail(400, 'validation_failed', 'Invalid', { messageKey: 'audit-remediation.errors.reasonRequired' });
      const body = parsed.data;
      if (OWNER_ONLY_STATUSES.includes(body.status) && !isOwner) throw fail(403, 'forbidden', 'Owner only', { messageKey: 'audit-remediation.errors.ownerOnlyStatus' });
      const f = finding(findingId);
      const version = versions.get(findingId) ?? 0;
      if (body.expectedVersion !== undefined && body.expectedVersion !== version) throw fail(409, 'conflict', 'Version', { reason: 'version_conflict', messageKey: 'audit-remediation.errors.versionConflict' });
      if (!canTransitionFinding(f.status, body.status, 'customer')) throw fail(409, 'conflict', 'Not allowed', { reason: 'transition_not_allowed', messageKey: 'audit-remediation.errors.transitionNotAllowed' });
      const at = now();
      const row: FindingStatusChange = { id: `fse_${++seq}`, jobId, findingId, from: f.status, to: body.status, reason: body.reason ?? null, actorId: userId, actorType: 'user', actor: 'customer', proposalId: null, at };
      history.set(findingId, [...(history.get(findingId) ?? []), row]);
      versions.set(findingId, version + 1);
      data = { ...data, findings: data.findings.map((x) => (x.id === findingId ? { ...x, status: body.status, statusReason: body.reason ?? null, statusUpdatedAt: at } : x)) };
      return { jobId, findingId, status: body.status, reason: body.reason ?? null, updatedBy: userId, updatedAt: at, version: version + 1 };
    },
    async listViews() { guard('listViews', []); return structuredClone(views); },
    async createView(raw) {
      guard('createView', [raw]);
      if (!canWrite) throw fail(403, 'forbidden', 'Forbidden');
      const body = CreateReportViewRequestSchema.parse(raw);
      if (views.length >= MAX_REPORT_VIEWS) throw fail(409, 'conflict', 'Limit', { reason: 'view_limit', messageKey: 'audit-remediation.views.limit' });
      if (nameTaken(body.name)) throw fail(409, 'conflict', 'Taken', { reason: 'view_name_taken', messageKey: 'audit-remediation.views.nameTaken' });
      const at = now();
      const v: ReportView = { ...body, id: `rv_${++seq}`, createdBy: userId, createdAt: at, updatedAt: at };
      if (v.isDefault) views = views.map((x) => ({ ...x, isDefault: false }));
      views = [...views, v];
      return structuredClone(v);
    },
    async updateView(id, raw) {
      guard('updateView', [id, raw]);
      const body = UpdateReportViewRequestSchema.parse(raw);
      const cur = view(id);
      if (!canWrite || (!isOwner && cur.createdBy !== userId)) throw fail(403, 'forbidden', 'Forbidden');
      if (body.name && nameTaken(body.name, id)) throw fail(409, 'conflict', 'Taken', { reason: 'view_name_taken', messageKey: 'audit-remediation.views.nameTaken' });
      if (body.isDefault) views = views.map((x) => ({ ...x, isDefault: false }));
      const next: ReportView = { ...cur, ...Object.fromEntries(Object.entries(body).filter(([, v]) => v !== undefined)), updatedAt: now() } as ReportView;
      views = views.map((x) => (x.id === id ? next : x));
      return structuredClone(next);
    },
    async deleteView(id) {
      guard('deleteView', [id]);
      const cur = view(id);
      if (!canWrite || (!isOwner && cur.createdBy !== userId)) throw fail(403, 'forbidden', 'Forbidden');
      views = views.filter((x) => x.id !== id);
    },
  };
  return {
    api, calls, state,
    get report() { return data; },
    get views() { return views; },
    failNext(m: keyof AdvancedReportApi) { failNext = m; },
  };
}
